import { RefObject, useEffect, useReducer, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import gql from 'graphql-tag'
import { useMutation } from '@apollo/client'
import { Modal } from '@collegebacker/backer-ui/ui'

import { CurrentFlow, IUser } from 'modules/contribution/types'
import { Fund, Step, User } from '../../types'
import GiftingSignup from './GiftingSignup'
import GiftingReturningGuest from './GiftingReturningGuest'
import { getQuery } from 'hooks'
import { isGiftFlow, isStarterGiftFlow } from 'modules/contribution/helpers'
import { hasActivePaymentMethod } from 'utils/user'
import { currentFlowValue } from 'modules/contribution/LocalState'
import fragments from 'modules/contribution/graphqlFragments'
import GiftingMessage from './GiftingMessage'
import GiftingAddPaymentMethod from './GiftingAddPaymentMethod'
import GiftingSelectPaymentMethod from './GiftingSelectPaymentMethod'
import GiftingConfirmation from './GiftingConfirmation'

import styles from './GiftingModal.module.scss'

const UPDATE_CURRENT_USER = gql`
  mutation UpdateCurrentUser($input: UpdateCurrentUserInputType!) {
    updateCurrentUser(input: $input) {
      ...ContributionUserFragment
    }
  }
  ${fragments.user}
`

export const getPreviousStep: any = {
  signup: '',
  'returning-guest': 'signup',
  message: '',
  'add-payment-method': 'message',
  'select-payment-method': 'confirmation',
  confirmation: 'message'
}

function reducer(_state: any, action: any): any {
  const { type, payload } = action

  if (type === 'setActiveStep') {
    return {
      activeStep: payload.activeStep,
      previousStep: payload.previousStep || getPreviousStep[payload.activeStep]
    }
  }
}

const GiftingModal = ({
  modalRef,
  fund,
  user,
  refetchCurrentUser
}: {
  modalRef: RefObject<null> | any
  fund: Fund
  user: IUser
  refetchCurrentUser: () => Promise<any>
}) => {
  const history = useHistory()
  const queryParams = getQuery()
  const modal = queryParams.get('modal')
  const step: Step = queryParams.get('step') as Step
  const { flow = currentFlowValue() }: { flow: CurrentFlow } = useParams()

  const [updateCurrentUser, { loading: loadingUpdateCurrentUser }] =
    useMutation(UPDATE_CURRENT_USER)

  const [modalMaxWidth, setModalMaxWidth] = useState(660)
  const [showModalNav, setShowModalNav] = useState(true)

  const initialState = {
    activeStep: step || 'signup',
    previousStep: ''
  }
  const [state, dispatch] = useReducer(reducer, initialState)
  const { activeStep, previousStep } = state

  useEffect(() => {
    activeStep === 'message' ? setModalMaxWidth(900) : setModalMaxWidth(660)
  }, [activeStep])

  const goBack = () => {
    if (
      activeStep === (user ? getFirstStepIfLoggedIn({ user, flow }) : 'signup')
    ) {
      return modalRef?.current?.close()
    }

    dispatch({
      type: 'setActiveStep',
      payload: {
        activeStep: previousStep
      }
    })
  }

  function renderStep(activeStep: Step) {
    switch (activeStep) {
      case 'signup':
        return (
          <GiftingSignup
            dispatch={dispatch}
            fund={fund}
            updateCurrentUser={updateCurrentUser}
            refetchCurrentUser={refetchCurrentUser}
            user={user}
          />
        )
      case 'returning-guest':
        return <GiftingReturningGuest fund={fund} />
      case 'message':
        return <GiftingMessage dispatch={dispatch} user={user} />
      case 'add-payment-method':
        return (
          <GiftingAddPaymentMethod
            user={user}
            fund={fund}
            updateCurrentUser={updateCurrentUser}
            setShowModalNav={setShowModalNav}
            onSuccess={() => {
              dispatch({
                type: 'setActiveStep',
                payload: {
                  activeStep: 'confirmation'
                }
              })
            }}
          />
        )
      case 'select-payment-method':
        return (
          <GiftingSelectPaymentMethod
            user={user}
            dispatch={dispatch}
            refetchCurrentUser={refetchCurrentUser}
          />
        )
      case 'confirmation':
        return (
          <GiftingConfirmation
            dispatch={dispatch}
            user={user}
            fund={fund}
            setShowModalNav={setShowModalNav}
          />
        )
    }
  }

  return (
    <Modal
      closeOnClickOutside={false}
      onCloseClick={() => {
        if (queryParams) {
          queryParams.delete('modal')
          queryParams.delete('step')
          queryParams.delete('frequency')
          history.replace({
            search: queryParams.toString()
          })
        }
      }}
      contentClassName={styles.modalContent}
      ref={modalRef}
      className={styles.modalContainer}
      style={
        {
          '--max-width': `${modalMaxWidth}px`
        } as any
      }
      showBackButton={showModalNav}
      showCloseButton={showModalNav}
      onBackClick={() => goBack()}
      isOpen={!!modal}
    >
      {renderStep(activeStep)}
    </Modal>
  )
}

export const getFirstStepIfLoggedIn = ({
  user,
  flow
}: {
  user: User
  flow: CurrentFlow
}): Step => {
  if (isGiftFlow(flow)) {
    if (isStarterGiftFlow(flow)) return 'beneficiary'
    return user.firstName && user.lastName ? 'message' : 'signup'
  } else {
    return hasActivePaymentMethod(user) ? 'confirmation' : 'add-payment-method'
  }
}

export default GiftingModal
